<template>
  <l-table
    ref="LTable"
    :module-name="MODULE_NAME"
    :table-columns="tableColumns"
    :filter-class="filterClass"
    :table-config-options="{ endpoint: `${MODULE_NAME}/${type}`, data: isUnMonitored() ? {id: this.$route.params.id} : {}}"
  >
    <template #create-button>
      <b-button
        v-if="isDoc()"
        v-b-toggle.sidebar-create-monitoring-report
        variant="primary"
      >
        {{ $t('Create') }}
      </b-button>
    </template>
    <template #before-search>
      <b-dropdown
        v-if="isInno()"
        variant="success"
        toggle-class="text-decoration-none"
        no-caret
        class="mr-2"
      >
        <template #button-content>
          <b-icon icon="file-earmark-excel" /> Excel
        </template>
        <download-file dispatch-action="staticModule/monitoringReportListQueue">
          {{ $t('Download') }}
        </download-file>
      </b-dropdown>
    </template>
    <template #cell(organization)="{data}">
      {{ $langName(data.value) }}
    </template>
    <template #cell(quarter)="{data}">
      {{ data.value.year + '/' + data.value.quarter }}
    </template>
    <template #cell(status)="{data}">
      <b-badge :variant="getStatusColor(data.value)">
        {{ $langName(getStatus(data.value).name) }}
      </b-badge>
    </template>
    <template #cell(actions)="{data}">
      <div class="text-nowrap">
        <feather-icon
          v-if="canMark(data.item)"
          v-b-toggle.sidebar-mark-report-monitoring
          icon="EditIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="mark(data.item)"
        />
        <feather-icon
          v-if="isUnMonitored()"
          v-b-toggle.sidebar-update-check-monitoring
          icon="EditIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="monitoring(data.item)"
        />
        <feather-icon
          v-b-toggle.sidebar-monitoring-report-files
          icon="FileIcon"
          class="cursor-pointer mr-1"
          size="16"
          :title="$t('Files')"
          @click="showFiles(data.item)"
        />
        <feather-icon
          v-if="canDelete(data.item)"
          icon="TrashIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="remove(data.item)"
        />
      </div>
    </template>
    <template #create-body>
      <create
        ref="createMonitoringReport"
        @refetchData="refetchData"
      />

      <mark-report-monitoring
        ref="markMonitoringReport"
        @refetchData="refetchData"
      />
      <monitoring-report-files />
      <check-monitoring-create @refetchData="addMonitoring()" />
    </template>

  </l-table>
</template>
<script>
import {
  BBadge, BButton, VBToggle, BDropdown,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import Ripple from 'vue-ripple-directive'
import Create from './Create.vue'
import LTable from '@/views/components/LTable.vue'
import store from '@/store'
import module from '@/store/monitoring-report'
import MonitoringReport from '@/views/components/filters/service/MonitoringReport'
import MarkReportMonitoring from '@/views/pages/monitoring-report/MarkReportMonitoring.vue'
import CheckMonitoringCreate from '@/views/pages/org-monitoring/components/check-monitoring/CheckMonitoringCreate.vue'
import MonitoringReportFiles from '@/views/pages/monitoring-report/MonitoringReportFiles.vue'
import DownloadFile from '@/views/pages/apply-document/components/DownloadFile.vue'

export default {
  name: 'MonitoringReport',
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    DownloadFile,
    MonitoringReportFiles,
    CheckMonitoringCreate,
    MarkReportMonitoring,
    BBadge,
    LTable,
    BButton,
    Create,
    BDropdown,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  computed: {
    statuses() {
      return this.$store.state[this.MODULE_NAME].statuses
    },
    monitoringReport() {
      return this.$store.state[this.MODULE_NAME].monitoringReport
    },
  },
  mounted() {
    this.$store.dispatch('staticModule/admissionYearPeriodList')
    this.$store.dispatch('staticModule/periodList')
    this.$store.dispatch('staticModule/universityList')
  },
  methods: {
    addMonitoring() {
      this.$emit('add')
      this.refetchData()
    },
    refetchData() {
      this.$refs.LTable.refetchData()
    },
    getStatusColor(status) {
      const select = this.statuses.find(item => item.id === Number(status))
      return select ? select.color : 'secondary'
    },
    getStatus(status) {
      return this.statuses.find(item => item.id === Number(status))
    },
    remove(data) {
      this.$removeSwal(this, data, `${this.MODULE_NAME}/del`, this.refetchData)
    },
    canDelete(reportMonitoring) {
      return (reportMonitoring.status === this.PENDING && this.isDoc())
    },
    canMark(reportMonitoring) {
      return reportMonitoring.status === this.PENDING && this.isAdvisor()
    },
    mark(reportMonitoring) {
      this.$store.commit(`${this.MODULE_NAME}/GET`, reportMonitoring)
    },
    showFiles(reportMonitoring) {
      this.$store.commit(`${this.MODULE_NAME}/GET`, reportMonitoring)
      this.$store.commit(`${this.MODULE_NAME}/FILES`, [])
      this.$store.dispatch(`${this.MODULE_NAME}/files`, reportMonitoring)
    },
    monitoring(reportMonitoring) {
      this.$store.commit(`${this.MODULE_NAME}/GET`, reportMonitoring)
    },
    isDoc() {
      return this.type === 'monitoringReportDoctorateList'
    },
    isAdvisor() {
      return this.type === 'monitoringReportAdvisorList'
    },
    isInno() {
      return this.type === 'monitoringReportInnovationList'
    },
    isUnMonitored() {
      return this.type === 'unmonitored'
    },
  },
  setup(props) {
    const MODULE_NAME = 'monitoringReport'
    const filterClass = new MonitoringReport(props.type)
    // Register module
    if (!store.hasModule(MODULE_NAME)) store.registerModule(MODULE_NAME, module)

    onUnmounted(() => {
      if (store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME, module)
    })
    const { PENDING } = store.state[MODULE_NAME]
    const tableColumns = [
      { key: 'index', label: '#' },
      { key: 'full_name', label: 'Full name' },
      { key: 'organization', label: 'Organization' },
      { key: 'course', label: 'Course' },
      { key: 'quarter', label: 'Quarter' },
      { key: 'status', label: 'Status' },
      { key: 'actions', label: 'Actions' },
    ]
    return {
      PENDING,
      MODULE_NAME,
      tableColumns,
      filterClass,
    }
  },
}
</script>
